import Pubnub from 'pubnub';

let connection;
let connectedChannels = {};

const messageSubscriptons = new Set();
const presenceSubscriptions = new Set();
const statusSubscriptions = new Set();

const handlePubnubError = (err) => {
    // console.log(`pubnub error: ${err}`);
    // is there anything we want to do with these errors?
    // we probably dont just want to swallow them
    throw err;
};

const realHandler = {
    message: function () {
        // @ts-ignore
        messageSubscriptons.forEach((handler) => handler.apply(undefined, arguments));
    },
    presence: function () {
        // @ts-ignore
        presenceSubscriptions.forEach((handler) => handler.apply(undefined, arguments));
    },
    status: function () {
        // @ts-ignore
        statusSubscriptions.forEach((handler) => handler.apply(undefined, arguments));
    },
    // status: statusEvent => {
    //     switch (statusEvent.category) {
    //         case 'PNDisconnectedCategory':
    //         case 'PNNetworkDownCategory':
    //             connect(); // reconnect
    //             break;
    //     }
    // },
};

/* eslint-disable @typescript-eslint/no-unused-vars */
// @ts-ignore
export const connect = async (pubnubId?: string, subscribeKey?: string) => {
    if (connection) {
        return connection;
    }

    connection = new Promise((resolve, reject) => {
        const options = {
            origin: 'pubnub.ably.io',
            publishKey: subscribeKey,
            restore: true,
            ssl: true,
            subscribeKey,
            uuid: pubnubId,
        };

        const pubnub = new Pubnub(options);

        const initialHandler = {
            status: (statusEvent) => {
                switch (statusEvent.category) {
                    case 'PNConnectedCategory':
                    case 'PNNetworkUpCategory':
                        resolve(pubnub);
                        break;
                    case 'PNDisconnectedCategory':
                    case 'PNNetworkDownCategory':
                        reject(new Error('Received a network-down message'));
                        break;
                    default:
                        return;
                }

                pubnub.removeListener(initialHandler);

                pubnub.addListener(realHandler);
            },
        };

        pubnub.addListener(initialHandler);

        return handshake(pubnub)
            .then(() => resolve({ pubnub, uuid: pubnubId }))
            .catch(reject);
    });

    return connection;
};

export const disconnect = () => {
    connect()
        .then(({ pubnub }) => {
            let connectedCount = 0;
            Object.keys(connectedChannels).forEach((k) => (connectedCount += connectedChannels[k].count));

            if (!connectedCount) {
                pubnub.removeListener(realHandler);
                pubnub.unsubscribeAll();
                pubnub.stop();
                messageSubscriptons.clear();
                presenceSubscriptions.clear();
                statusSubscriptions.clear();
                connectedChannels = {};
                connection = null;
            }
        })
        .catch(handlePubnubError);
};

const handshake = (pubnub) =>
    new Promise((resolve, reject) => {
        pubnub.time((status) => {
            if (status.error) {
                reject(new Error(`Pubnub service failed to respond to time request: ${status}`));
            } else {
                resolve(pubnub);
            }
        });
    });

export const subscribe = async (
    channel: string,
    presenceHandler: Function,
    messageHandler: Function,
    statusHandler: Function,
    presenceOnly: boolean = false,
    withPresence: boolean = false
) => {
    const data = connectedChannels[channel] || { count: 0 };
    connectedChannels[channel] = data;

    const initial = data.count;

    data.count = data.count > 0 ? (data.count += 1) : (data.count = 1);

    const shouldSubscribe = initial === 0 && data.count === 1;

    if (shouldSubscribe) {
        messageSubscriptons.add(messageHandler);
        presenceSubscriptions.add(presenceHandler);
        statusSubscriptions.add(statusHandler);

        data.messageHandler = messageHandler;
        data.presenceHandler = presenceHandler;
        data.statusHandler = statusHandler;

        if (presenceOnly) {
            await connect()
                .then(({ pubnub }) => {
                    pubnub.subscribe({
                        channels: [`${channel}-pnpres`],
                    });
                })
                .catch(handlePubnubError);
        } else {
            await connect()
                .then(({ pubnub }) => {
                    pubnub.subscribe({
                        channels: [channel],
                        withPresence,
                    });
                })
                .catch(handlePubnubError);
        }
    }

    return {
        unsubscribe: () => {
            const data = connectedChannels[channel];
            if (data.count > 0) {
                data.count -= 1;
            }
            const shouldUnsubscribe = data.count === 0;
            if (shouldUnsubscribe) {
                messageSubscriptons.delete(data.messageHandler);
                presenceSubscriptions.delete(data.presenceHandler);
                statusSubscriptions.delete(data.statusHandler);

                if (presenceOnly) {
                    return connect()
                        .then(({ pubnub }) => {
                            pubnub.unsubscribe({ channels: [`${channel}-pnpres`] });
                        })
                        .catch(handlePubnubError);
                } else {
                    return connect()
                        .then(({ pubnub }) => {
                            pubnub.unsubscribe({ channels: [channel] });
                        })
                        .catch(handlePubnubError);
                }
            }
        },
    };
};

export const hereNow = (channel: string) =>
    new Promise<any>((resolve, reject) => {
        connect()
            .then(({ pubnub }) =>
                pubnub.hereNow(
                    {
                        channels: [channel],
                        includeState: false,
                        includeUUIDs: false,
                    },
                    (status, response) => {
                        if (status.error) {
                            reject(status.category);
                        } else {
                            resolve(response);
                        }
                    }
                )
            )
            .catch(handlePubnubError);
    });

export const setFilterExpression = (filterExpression: string) =>
    new Promise<void>((resolve) => {
        connect()
            .then(({ pubnub }) => {
                pubnub.setFilterExpression(filterExpression);
                resolve(null);
            })
            .catch(handlePubnubError);
    });

export const keepalive = () =>
    new Promise<any>((resolve, reject) => {
        connect()
            .then(({ pubnub }) =>
                handshake(pubnub)
                    // @ts-ignore
                    .then(resolve())
                    .catch((err) => reject(err))
            )
            .catch((err) => reject(err));
    });
